import { showToast } from '@provi/provi-components'
import { useState, useEffect, useCallback } from 'react'

import { POSTHOG_EVENTS } from '../../constants/posthog'
import {
  getNegotiationSimulation,
  getNegotiationAwaitingSignature,
  getNegotiationPayment,
  checkInvoicePaymentStatus,
  acceptNegotiationProposal,
  signNegotiationTerm,
  getFinancingInvoices
} from '../../services/api'
import { captureEvent } from '../../utils/posthog'

import {
  formatCurrency,
  formatDate,
  formatPercentage,
  formatInstallmentsSummary,
  formatCPF,
  mapSignatureStatus,
  cloneCalculationData,
  copyToClipboard
} from './helpers'

const NEGOTIATION_INVOICE_ID_KEY = 'negotiation_invoice_id'
const INVOICE_CHECK_POLL_INTERVAL_MS = 1 * 60 * 1000 // 1 minuto

/**
 * Hook para obter dados da simulação de negociação
 * @param {string} crid - Credit Request ID
 * @returns {object} Estado e dados da simulação
 */
export const useNegotiationSimulation = crid => {
  const [data, setData] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedEntry, setSelectedEntry] = useState(null)
  const [selectedInstallment, setSelectedInstallment] = useState(null)
  const [customUpFront, setCustomUpFront] = useState(null)
  const [isAccepting, setIsAccepting] = useState(false)

  const fetchData = useCallback(
    async upFrontValue => {
      if (!crid) return

      setIsLoading(true)
      setError(null)

      try {
        const response = await getNegotiationSimulation(crid, upFrontValue)
        setData(response)

        // Seleciona opções recomendadas por padrão
        const recommendedEntry = response.entryOptions?.find(opt => opt.isRecommended) || response.entryOptions?.[0]
        const recommendedInstallment =
          response.installmentOptions?.find(opt => opt.isRecommended) || response.installmentOptions?.[0]

        setSelectedEntry(recommendedEntry)
        setSelectedInstallment(recommendedInstallment)

        captureEvent(POSTHOG_EVENTS.SIMULACAO_NEGOCIACAO_REALIZADA, {
          numero_pedido_compra: String(crid),
          tipo_negociacao: 'renegociação',
          saldo_devedor: response.summary?.originalRefinanceValue,
          valor_entrada_informado: upFrontValue ?? recommendedEntry?.value ?? null,
          saldo_devedor_negociado: recommendedInstallment?.totalValue,
          valor_parcela: recommendedInstallment?.installmentValue,
          parcelamento_informado: recommendedInstallment?.installments,
          total_pago_a_prazo: recommendedInstallment?.refinancingValue,
          ajuste_manual_entrada: !!upFrontValue,
          resultado_simulacao: 'sucesso'
        })
      } catch (err) {
        setError(err)

        captureEvent(POSTHOG_EVENTS.SIMULACAO_NEGOCIACAO_REALIZADA, {
          numero_pedido_compra: String(crid),
          tipo_negociacao: 'renegociação',
          saldo_devedor: null,
          valor_entrada_informado: upFrontValue ?? null,
          saldo_devedor_negociado: null,
          valor_parcela: null,
          parcelamento_informado: null,
          total_pago_a_prazo: null,
          ajuste_manual_entrada: !!upFrontValue,
          resultado_simulacao: 'erro'
        })

        if (err?.response?.status !== 401) {
          showToast('Erro ao carregar simulação. Tente novamente.')
        }
      } finally {
        setIsLoading(false)
      }
    },
    [crid]
  )

  const refetchWithUpFront = useCallback(
    upFrontValue => {
      setCustomUpFront(upFrontValue)
      fetchData(upFrontValue)
    },
    [fetchData]
  )

  useEffect(() => {
    fetchData()
  }, [fetchData])

  /**
   * Aceita a proposta de negociação
   * @param {object} params - Parâmetros
   * @param {boolean} params.isQuitation - Se é pagamento à vista (quitação)
   * @param {Function} params.onSuccess - Callback de sucesso com resposta da API
   */
  const handleAcceptProposal = useCallback(
    async ({ isQuitation = false, onSuccess }) => {
      if (!data) return

      const negotiationId = isQuitation ? data.cashNegotiationId : data.installmentNegotiationId
      const upFront = isQuitation ? data.summary?.cashPaymentValue : selectedEntry?.value
      const installmentNumber = isQuitation ? 0 : selectedInstallment?.installments || 0

      setIsAccepting(true)
      try {
        const response = await acceptNegotiationProposal({
          creditRequestId: Number(crid),
          upFront: upFront || 0,
          negotiationId: negotiationId || '',
          installmentNumber
        })

        if (onSuccess) {
          onSuccess(response, isQuitation)
        }
      } catch (err) {
        if (err?.response?.status !== 401) {
          showToast('Erro ao aceitar proposta. Tente novamente.')
        }
      } finally {
        setIsAccepting(false)
      }
    },
    [crid, data, selectedEntry, selectedInstallment]
  )

  // Dados formatados para exibição
  const formattedData = data
    ? {
        saldoDevedorAtual: formatCurrency(
          selectedInstallment?.outstandingDebt !== undefined
            ? selectedInstallment.outstandingDebt
            : data.summary?.outstandingDebt
        ),
        saldoDevedorAnterior: formatCurrency(data.summary?.previousOutstandingDebt),
        percentualDesconto: data.summary?.discountPercentage,
        percentualDescontoAVista: data.summary?.cashPaymentDiscountPercentage || 0,
        calculation: cloneCalculationData(data.summary?.calculation),
        calculationUrl: data.summary?.calculationUrl,

        valorPagamentoAVista: formatCurrency(data.summary?.cashPaymentValue),
        descontoPagamentoAVista:
          data.summary?.cashPaymentDiscount > 0 ? formatCurrency(data.summary.cashPaymentDiscount) : null,
        validadeProposta: formatDate(
          data.summary?.cashPaymentDueDate || data.agreement?.entryDueDate || data.summary?.proposalExpirationDate
        ),
        dataVencimentoPagamentoAVista: formatDate(data.summary?.cashPaymentDueDate || data.agreement?.entryDueDate),

        valorEntradaSelecionada: formatCurrency(selectedEntry?.value),
        dataVencimentoEntrada: formatDate(data.agreement?.entryDueDate || selectedEntry?.dueDate),
        badgeEntrada: customUpFront != null ? '' : selectedEntry?.badge || '',
        valorMinimoEntrada: data.summary?.minEntryValue,
        validacaoEntrada: data.summary?.entryValidation,

        opcoesParcelas: data.installmentOptions?.map(opt => ({
          id: opt.id,
          label: formatInstallmentsSummary(opt.installments, opt.installmentValue),
          isRecommended: opt.isRecommended
        })),
        opcaoParcelamentoSelecionada: selectedInstallment
          ? formatInstallmentsSummary(selectedInstallment.installments, selectedInstallment.installmentValue)
          : '',
        quantidadeParcelas: selectedInstallment?.installments,
        valorParcela: formatCurrency(selectedInstallment?.installmentValue),
        taxaJuros: selectedInstallment?.interestRate
          ? `${selectedInstallment.interestRate.toFixed(2).replace('.', ',')}% a.m.`
          : formatPercentage(data.summary?.interestRate, 'a.m.'),

        dataVencimentoPrimeiraParcela: formatDate(selectedInstallment?.firstDueDate || data.agreement?.firstInstallmentDueDate),
        totalPagoAPrazo: formatCurrency(selectedInstallment?.totalValue || data.agreement?.totalValue),

        refinancingValue: formatCurrency(data.summary?.refinanceValue),
        interestRate: data.summary?.interestRate,

        installmentNegotiationId: data.installmentNegotiationId,
        cashNegotiationId: data.cashNegotiationId
      }
    : null

  return {
    data,
    formattedData,
    isLoading,
    error,
    selectedEntry,
    setSelectedEntry,
    selectedInstallment,
    setSelectedInstallment,
    refetch: fetchData,
    refetchWithUpFront,
    customUpFront,
    isAccepting,
    handleAcceptProposal
  }
}

/**
 * Hook para obter dados de assinatura de negociação
 * @param {string} crid - Credit Request ID
 * @returns {object} Estado e dados da assinatura
 */
export const useNegotiationSignature = crid => {
  const [data, setData] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isSigning, setIsSigning] = useState(false)

  const fetchData = useCallback(async () => {
    if (!crid) return

    setIsLoading(true)
    setError(null)

    try {
      const response = await getNegotiationAwaitingSignature(crid)
      setData(response)
    } catch (err) {
      setError(err)
      if (err?.response?.status !== 401) {
        showToast('Erro ao carregar dados. Tente novamente.')
      }
    } finally {
      setIsLoading(false)
    }
  }, [crid])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  /**
   * Assina o termo de negociação e determina o redirect baseado na resposta
   * @param {Function} onSuccess - Callback que recebe a URL de redirect
   *
   * Fluxos:
   * 1. Quitação (à vista): não passa por assinatura
   * 2. Parcelado COM avalista: aguarda assinatura do avalista
   * 3. Parcelado SEM avalista: chama invoices v2 para gerar entrada
   */
  const handleSign = useCallback(
    async onSuccess => {
      setIsSigning(true)
      try {
        // Recuperar dados salvos da simulação (para pagamento parcelado)
        const negotiationId = localStorage.getItem('negotiationId')
        const selectedEntryStr = localStorage.getItem('negotiation_selected_entry')
        const selectedInstallmentStr = localStorage.getItem('negotiation_selected_installment')

        // Se temos dados salvos, precisamos primeiro aceitar a proposta
        if (selectedEntryStr && selectedInstallmentStr) {
          const selectedEntry = JSON.parse(selectedEntryStr)
          const selectedInstallment = JSON.parse(selectedInstallmentStr)

          // Chamar accept antes de assinar (para pagamento parcelado)
          await acceptNegotiationProposal({
            creditRequestId: Number(crid),
            upFront: selectedEntry?.value || 0,
            negotiationId: negotiationId || '',
            installmentNumber: selectedInstallment?.installments || 0
          })

          // Salvar dados formatados de entrada e parcelas para a tela de pagamento
          if (selectedEntry?.value) {
            const formattedEntryValue = formatCurrency(selectedEntry.value)
            localStorage.setItem('negotiation_entry_value', formattedEntryValue)
          }

          if (selectedEntry?.dueDate) {
            const formattedEntryDate = formatDate(selectedEntry.dueDate)
            localStorage.setItem('negotiation_entry_due_date', formattedEntryDate)
          }

          // Salvar dados de parcelas formatados (apenas para pagamento parcelado)
          const installmentNumber = selectedInstallment?.installments || 0
          if (installmentNumber > 0) {
            if (selectedInstallment?.installments) {
              localStorage.setItem('negotiation_installments_count', selectedInstallment.installments.toString())
            }

            if (selectedInstallment?.installmentValue) {
              const formattedInstallmentValue = formatCurrency(selectedInstallment.installmentValue)
              localStorage.setItem('negotiation_installment_value', formattedInstallmentValue)
            }
          } else {
            // Limpar dados de parcelas se for pagamento à vista
            localStorage.removeItem('negotiation_installments_count')
            localStorage.removeItem('negotiation_installment_value')
          }

          // Limpar dados do localStorage após aceitar
          localStorage.removeItem('negotiation_selected_entry')
          localStorage.removeItem('negotiation_selected_installment')
        }

        const device = navigator?.userAgent || 'unknown'

        let signatureStatus = null
        let payment = null
        let studentRecipient = null
        let guarantorRecipient = null

        try {
          const { data } = await signNegotiationTerm(Number(crid), device)

          signatureStatus = data
          payment = data?.payment
          studentRecipient = signatureStatus?.recipients?.find(r => r.signer === 'User')
          guarantorRecipient = signatureStatus?.recipients?.find(r => r.signer === 'Guarantor')

          const hasGuarantor = !!signatureStatus?.hasGuarantor

          captureEvent(POSTHOG_EVENTS.TERMO_CONFISSAO_DIVIDA_ASSINADO, {
            numero_pedido_compra: String(crid),
            id_negociacao: String(signatureStatus?.RenegotiationProposalId),
            id_contrato: String(signatureStatus?.ProviContractId),
            tem_avalista: hasGuarantor,
            papel_assinante: 'aluno',
            status_assinatura: signatureStatus?.isContractSigned ? 'concluida' : 'pendente',
            assinatura_aluno_concluida: studentRecipient?.signed ?? false,
            canal_assinatura: 'app_aluno',
            ...(hasGuarantor ? { assinatura_avalista_concluida: guarantorRecipient?.signed ?? false } : {})
          })
        } catch (signErr) {
          captureEvent(POSTHOG_EVENTS.TERMO_CONFISSAO_DIVIDA_ASSINADO, {
            numero_pedido_compra: String(crid),
            id_negociacao: null,
            id_contrato: null,
            tem_avalista: null,
            papel_assinante: 'aluno',
            status_assinatura: 'erro',
            assinatura_aluno_concluida: false,
            canal_assinatura: 'app_aluno'
          })
          throw signErr
        }

        let redirectUrl = '/dashboard/negociacao/aguardando-assinatura'

        if (signatureStatus?.hasGuarantor) {
          // Fluxo 2: Parcelado COM avalista
          if (guarantorRecipient && !guarantorRecipient.signed) {
            // Avalista ainda não assinou - aguardar assinatura
            redirectUrl = '/dashboard/negociacao/aguardando-assinatura'
          } else if (payment) {
            // Avalista já assinou e tem payment - ir para pagamento
            redirectUrl = '/dashboard/negociacao/pagamento'
          }
        } else {
          // Fluxo 3: Parcelado SEM avalista - chamar invoices v2 para gerar entrada
          try {
            await getFinancingInvoices(crid)
            // Após gerar entrada via invoices v2, redirecionar para pagamento
            redirectUrl = '/dashboard/negociacao/pagamento'
          } catch (invoicesError) {
            console.error('Erro ao gerar faturas via invoices v2:', invoicesError)
            // Fallback: se tem payment na resposta original, ir para pagamento
            if (payment) {
              redirectUrl = '/dashboard/negociacao/pagamento'
            }
          }
        }

        if (onSuccess) {
          onSuccess(redirectUrl)
        }
      } catch (err) {
        if (err?.response?.status !== 401) {
          showToast('Erro ao assinar termo. Tente novamente.')
        }
      } finally {
        setIsSigning(false)
      }
    },
    [crid]
  )

  const formattedData = data
    ? {
        saldoDevedorNegociado: formatCurrency(data.summary?.outstandingDebt),
        valorARefinanciar: formatCurrency(data.summary?.refinanceValue),
        validadeDaProposta: formatDate(data.summary?.proposalExpirationDate),

        valorEntrada: formatCurrency(data.agreement?.entryValue),
        novasParcelasResumo: formatInstallmentsSummary(data.agreement?.installmentsCount, data.agreement?.installmentValue),
        vencimentoPrimeiraParcela: formatDate(data.agreement?.firstInstallmentDueDate),
        taxaJurosContrato: formatPercentage(data.summary?.interestRate, 'a.m.'),

        dataVencimentoEntrada: formatDate(data.agreement?.entryDueDate),
        parcelas: data.installments?.map(inst => ({
          label: inst.label,
          valor: formatCurrency(inst.value),
          data: formatDate(inst.dueDate)
        })),

        totalPagoAPrazo: formatCurrency(data.agreement?.totalValue),

        termUrl: data.termUrl,
        calculationUrl: data.summary?.calculationUrl,

        saldoDevedorAtual: formatCurrency(data.summary?.outstandingDebt),
        saldoDevedorAnterior: formatCurrency(data.summary?.previousOutstandingDebt),
        percentualDesconto: data.summary?.discountPercentage,
        calculation: cloneCalculationData(data.summary?.calculation),
        quantidadeParcelas: data.agreement?.installmentsCount,
        valorParcela: formatCurrency(data.agreement?.installmentValue),
        dataVencimentoPrimeiraParcela: formatDate(data.agreement?.firstInstallmentDueDate)
      }
    : null

  return {
    data,
    formattedData,
    isLoading,
    error,
    isSigning,
    handleSign,
    refetch: fetchData
  }
}

/**
 * Hook para obter dados de aguardando assinatura
 * @param {string} crid - Credit Request ID
 * @returns {object} Estado e dados de aguardando assinatura
 */
export const useNegotiationAwaitingSignature = crid => {
  const [data, setData] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchData = useCallback(async () => {
    if (!crid) return

    setIsLoading(true)
    setError(null)

    try {
      const response = await getNegotiationAwaitingSignature(crid)
      setData(response)
    } catch (err) {
      setError(err)
      if (err?.response?.status !== 401) {
        showToast('Erro ao carregar dados. Tente novamente.')
      }
    } finally {
      setIsLoading(false)
    }
  }, [crid])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const studentStatus = data?.student ? mapSignatureStatus(data.student.status) : null
  const guarantorStatus = data?.guarantor ? mapSignatureStatus(data.guarantor.status) : null
  // Dados formatados para exibição
  const formattedData = data
    ? {
        nomeAluno: data.student?.name,
        cpfAluno: formatCPF(data.student?.cpf),
        emailAluno: data.student?.email,
        statusAssinaturaAlunoVariant: studentStatus?.variant,
        statusAssinaturaAlunoTexto: studentStatus?.text,

        nomeAvalista: data.guarantor?.name,
        cpfAvalista: formatCPF(data.guarantor?.cpf),
        emailAvalista: data.guarantor?.email,
        statusAssinaturaAvalistaVariant: guarantorStatus?.variant,
        statusAssinaturaAvalistaTexto: guarantorStatus?.text,
        hasGuarantor: !!data.guarantor,

        saldoDevedorAtual: formatCurrency(data.summary?.outstandingDebt),
        saldoDevedorAnterior: formatCurrency(data.summary?.previousOutstandingDebt),
        percentualDesconto: data.summary?.discountPercentage,
        calculation: cloneCalculationData(data.summary?.calculation),
        calculationUrl: data.summary?.calculationUrl,
        valorEntrada: formatCurrency(data.agreement?.entryValue),
        quantidadeParcelas: data.agreement?.installmentsCount,
        valorParcela: formatCurrency(data.agreement?.installmentValue),
        totalPagoAPrazo: formatCurrency(data.agreement?.totalValue),
        dataVencimentoEntrada: formatDate(data.agreement?.entryDueDate),
        dataVencimentoPrimeiraParcela: formatDate(data.agreement?.firstInstallmentDueDate)
      }
    : null

  return {
    data,
    formattedData,
    isLoading,
    error,
    refetch: fetchData
  }
}

/**
 * Hook para obter dados de pagamento
 * @param {string} crid - Credit Request ID
 * @returns {object} Estado e dados de pagamento
 */
export const useNegotiationPayment = crid => {
  const [data, setData] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isCopying, setIsCopying] = useState(false)
  const [isPaid, setIsPaid] = useState(false)

  const fetchData = useCallback(async () => {
    if (!crid) return

    setIsLoading(true)
    setError(null)

    try {
      const response = await getNegotiationPayment(crid)
      setData(response)
      setIsPaid(false)

      const hasPix = !!response?.paymentMethods?.pix?.copyPasteCode
      const hasBoleto = !!response?.paymentMethods?.boleto?.barcode
      if (!hasPix && !hasBoleto) {
        showToast('Houve um problema ao gerar o pagamento. Por favor, tente novamente mais tarde.')
      }

      if (response?.InvoiceId != null && response.InvoiceId !== '') {
        localStorage.setItem(NEGOTIATION_INVOICE_ID_KEY, String(response.InvoiceId))
      }
    } catch (err) {
      setError(err)
      if (err?.response?.status !== 401) {
        showToast('Houve um problema ao gerar o pagamento. Por favor, tente novamente mais tarde.')
      }
    } finally {
      setIsLoading(false)
    }
  }, [crid])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  useEffect(() => {
    const invoiceId = data?.InvoiceId != null && data.InvoiceId !== '' ? data.InvoiceId : null
    if (!invoiceId || isPaid) return

    const checkPaid = async () => {
      try {
        const result = await checkInvoicePaymentStatus(invoiceId)
        if (result && result.paid === true) {
          setIsPaid(true)
        }
      } catch {}
    }

    checkPaid()
    const intervalId = setInterval(checkPaid, INVOICE_CHECK_POLL_INTERVAL_MS)
    return () => clearInterval(intervalId)
  }, [data?.InvoiceId, isPaid])

  const handleCopyPix = useCallback(async () => {
    if (!data?.paymentMethods?.pix?.copyPasteCode) return

    setIsCopying(true)
    try {
      await copyToClipboard(data.paymentMethods.pix.copyPasteCode)
      showToast('Código PIX copiado!', 'success')
    } catch (err) {
      showToast('Erro ao copiar código PIX')
    } finally {
      setIsCopying(false)
    }
  }, [data?.paymentMethods?.pix?.copyPasteCode])

  const handleCopyBarcode = useCallback(async () => {
    if (!data?.paymentMethods?.boleto?.barcode) return

    setIsCopying(true)
    try {
      await copyToClipboard(data.paymentMethods.boleto.barcode)
      showToast('Código de barras copiado!', 'success')
    } catch (err) {
      showToast('Erro ao copiar código de barras')
    } finally {
      setIsCopying(false)
    }
  }, [data?.paymentMethods?.boleto?.barcode])

  const formattedData = data
    ? {
        valorEntrada: formatCurrency(data.agreement?.entryValue),
        dataVencimentoEntrada: formatDate(data.agreement?.entryDueDate),
        qrCodePixImage: data.paymentMethods?.pix?.qrCodeUrl || data.paymentMethods?.pix?.qrCodeBase64,
        codigoPix: data.paymentMethods?.pix?.copyPasteCode,
        urlFaturaBoleto: data.paymentMethods?.boleto?.boletoUrl,
        codigoDeBarrasBoleto: data.paymentMethods?.boleto?.barcode,
        saldoDevedorAtual: formatCurrency(data.summary?.outstandingDebt),
        saldoDevedorAnterior: formatCurrency(data.summary?.previousOutstandingDebt),
        percentualDesconto: data.summary?.discountPercentage,
        calculation: cloneCalculationData(data.summary?.calculation),
        calculationUrl: data.summary?.calculationUrl,
        quantidadeParcelas: data.agreement?.installmentsCount,
        valorParcela: formatCurrency(data.agreement?.installmentValue),
        totalPagoAPrazo: formatCurrency(data.agreement?.totalValue),
        dataVencimentoPrimeiraParcela: formatDate(data.agreement?.firstInstallmentDueDate)
      }
    : null

  return {
    data,
    formattedData,
    isLoading,
    error,
    isCopying,
    isPaid,
    handleCopyPix,
    handleCopyBarcode,
    refetch: fetchData
  }
}

/**
 * Hook para obter dados de pagamento concluído com sucesso
 * @param {string} crid - Credit Request ID
 * @returns {object} Estado e dados do pagamento concluído
 */
export const useNegotiationPaymentSuccess = crid => {
  const [data, setData] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchData = useCallback(async () => {
    if (!crid) return

    setIsLoading(true)
    setError(null)

    try {
      const response = await getNegotiationPayment(crid)
      setData(response)
    } catch (err) {
      setError(err)
      if (err?.response?.status !== 401) {
        showToast('Erro ao carregar dados. Tente novamente.')
      }
    } finally {
      setIsLoading(false)
    }
  }, [crid])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // Dados formatados para exibição da página de sucesso
  const formattedData = data
    ? {
        tipoPagamento: data.paymentType === 'cash' ? 'avista' : 'parcelado',

        valorEntrada: formatCurrency(data.agreement?.entryValue),
        quantidadeParcelas: data.agreement?.installmentsCount || 0,
        valorParcela: formatCurrency(data.agreement?.installmentValue),
        totalPagoAPrazo: formatCurrency(data.agreement?.totalValue),

        totalAVista: formatCurrency(data.summary?.outstandingDebt),
        dataVencimentoPagamento: formatDate(data.agreement?.paymentDueDate || data.agreement?.entryDueDate),

        dataVencimentoEntrada: formatDate(data.agreement?.entryDueDate),
        dataVencimentoPrimeiraParcela: formatDate(data.agreement?.firstInstallmentDueDate),

        saldoDevedorAtual: formatCurrency(data.summary?.outstandingDebt),
        saldoDevedorAnterior: formatCurrency(data.summary?.previousOutstandingDebt),

        calculation: cloneCalculationData(data.summary?.calculation)
      }
    : null

  return {
    data,
    formattedData,
    isLoading,
    error,
    refetch: fetchData
  }
}
